import { useState, useEffect, useCallback } from "react";
import { emptyLead } from "../constants/leads";
import { uid } from "../utils/storage";
import { validateLeadForm } from "../utils/validation";
import { leadsService } from "../services/leadsService";
import { eventsService } from "../services/eventsService";
import { voiceService } from "../services/voiceService";
import { useAuth } from "../contexts";
import { useToast } from "../components/Toast";
import { clearNotesBackup } from "../components/AINotesButton";

/**
 * Transform API lead response to app format
 */
function transformApiLeadToApp(apiLead) {
  return {
    id: apiLead.id,
    managerTg: apiLead.manager_username ? `@${apiLead.manager_username}` : `user_${apiLead.manager_telegram_id}`,
    managerName: apiLead.manager_name,
    event: apiLead.event,
    name: apiLead.name,
    company: apiLead.company || "",
    contact: apiLead.contact,
    leadType: apiLead.lead_type,
    customLeadType: apiLead.custom_lead_type || "",
    trafficSource: Array.isArray(apiLead.traffic_source) ? apiLead.traffic_source : [],  // Массив
    customTraffic: apiLead.custom_traffic_source || "",
    worksWithUs: apiLead.works_with_us,
    commissionModel: apiLead.commission_model,
    customCommission: apiLead.custom_commission_model || "",
    photoDataUrls: apiLead.photos.map(photo => photo.url), // Presigned URLs from API
    notes: apiLead.notes || "",
    voiceNoteUrl: apiLead.voice_note?.url || null, // Voice note URL
    voiceNoteDuration: apiLead.voice_note?.duration || 0, // Voice note duration
    voiceNoteMimeType: apiLead.voice_note?.mime_type || '', // Voice note MIME type
    notificationEnabled: apiLead.notification_enabled || false,
    notificationDate: apiLead.notification_date || null,
    notificationMessage: apiLead.notification_message || '',
    // New fields
    status: apiLead.status || "to_process",
    priority: apiLead.priority || null,
    partner_id: apiLead.partner_id || "",
    contact_telegram: apiLead.contact_telegram || "",
    contact_whatsapp: apiLead.contact_whatsapp || "",
    contact_wechat: apiLead.contact_wechat || "",
    contact_email: apiLead.contact_email || "",
    contact_phone: apiLead.contact_phone || "",
    createdAt: apiLead.created_at,
    updatedAt: apiLead.updated_at,
  };
}

export function useLeadsLogic(managerTg, managerName) {
  const { user } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("home");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => ({ ...emptyLead, id: uid(), managerTg, managerName }));
  const [defaultEvent, setDefaultEvent] = useState(null);
  
  // Voice note state
  const [voiceNoteBlob, setVoiceNoteBlob] = useState(null);
  const [voiceNoteDuration, setVoiceNoteDuration] = useState(0);
  const [voiceNoteMimeType, setVoiceNoteMimeType] = useState('');

  /**
   * Load default event from API
   */
  const loadDefaultEvent = useCallback(async () => {
    try {
      console.log('[useLeadsLogic] Loading default event...');
      const events = await eventsService.getEvents();
      console.log('[useLeadsLogic] All events:', events);
      // Filter active events and sort by display_order
      const activeEvents = events
        .filter(e => e.is_active)
        .sort((a, b) => a.display_order - b.display_order);
      console.log('[useLeadsLogic] Active events (sorted):', activeEvents);
      // The first event after sorting is the default one (display_order 0 or 1)
      const defaultEv = activeEvents[0];
      console.log('[useLeadsLogic] Default event (first in sorted list):', defaultEv);
      if (defaultEv) {
        setDefaultEvent(defaultEv.name);
        console.log('[useLeadsLogic] Set default event to:', defaultEv.name);
      } else {
        console.log('[useLeadsLogic] No active events found');
      }
    } catch (err) {
      console.error("[useLeadsLogic] Failed to load default event:", err);
    }
  }, []);

  // Load default event on mount
  useEffect(() => {
    loadDefaultEvent();
  }, [loadDefaultEvent]);

  /**
   * Load leads from API
   */
  const loadLeads = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await leadsService.getLeads({
        sort_by: "created_at",
        sort_order: "desc",
        limit: 100,
      });

      const transformedLeads = response.leads.map(transformApiLeadToApp);
      setLeads(transformedLeads);
    } catch (err) {
      console.error("Failed to load leads:", err);
      setError(err.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Load single lead by ID (for deep linking)
   */
  const loadSingleLead = useCallback(async (leadId) => {
    try {
      setLoading(true);
      setError(null);

      const apiLead = await leadsService.getLead(leadId);
      const transformedLead = transformApiLeadToApp(apiLead);
      
      return transformedLead;
    } catch (err) {
      console.error("Failed to load lead:", err);
      setError(err.message || "Failed to load lead");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load leads on mount and when user changes
  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  function resetForm() {
    setForm({ 
      ...emptyLead, 
      id: uid(), 
      managerTg, 
      managerName,
      event: defaultEvent || emptyLead.event // Use default event from API or fallback
    });
    setEditingId(null);
    // Reset voice note state
    setVoiceNoteBlob(null);
    setVoiceNoteDuration(0);
    setVoiceNoteMimeType('');
    // Clear AI notes backup from localStorage
    clearNotesBackup();
  }

  function startAdd(photos = [], aiData = null) {
    // Clear AI notes backup from localStorage
    clearNotesBackup();
    
    // Сначала сбрасываем форму до дефолтного состояния
    const baseForm = { 
      ...emptyLead, 
      id: uid(), 
      managerTg, 
      managerName,
      event: defaultEvent || emptyLead.event
    };
    
    console.log('[useLeadsLogic] startAdd called');
    console.log('[useLeadsLogic] defaultEvent:', defaultEvent);
    console.log('[useLeadsLogic] baseForm.event:', baseForm.event);
    console.log('[useLeadsLogic] photos:', photos);
    console.log('[useLeadsLogic] aiData:', aiData);
    
    // Если есть AI данные, предзаполняем форму
    if (aiData) {
      console.log('AI data received:', aiData);
      setForm({
        ...baseForm,
        name: aiData.name || baseForm.name,
        company: aiData.company || baseForm.company,
        contact: aiData.contact || baseForm.contact,
        notes: aiData.notes || baseForm.notes,
        // Новые поля контактов из AI
        contact_telegram: aiData.contact_telegram || baseForm.contact_telegram,
        contact_whatsapp: aiData.contact_whatsapp || baseForm.contact_whatsapp,
        contact_wechat: aiData.contact_wechat || baseForm.contact_wechat,
        contact_email: aiData.contact_email || baseForm.contact_email,
        contact_phone: aiData.contact_phone || baseForm.contact_phone,
        photoDataUrls: photos
      });
    } else if (photos.length > 0) {
      // Если только фото без AI данных
      setForm({
        ...baseForm,
        photoDataUrls: photos
      });
    } else {
      // Пустая форма
      setForm(baseForm);
    }
    
    setEditingId(null);
    setView("add");
  }

  function startEdit(id) {
    // Clear AI notes backup from localStorage
    clearNotesBackup();
    
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    
    console.log('[useLeadsLogic] startEdit - Full lead object:', lead);
    console.log('[useLeadsLogic] startEdit - voiceNoteUrl:', lead.voiceNoteUrl);
    console.log('[useLeadsLogic] startEdit - voiceNoteDuration:', lead.voiceNoteDuration);
    console.log('[useLeadsLogic] startEdit - voiceNoteMimeType:', lead.voiceNoteMimeType);
    
    const photoDataUrls = lead.photoDataUrls || [];
    
    setForm({
      ...emptyLead,
      ...lead,
      photoDataUrls,
      customEvent: "",
      customLeadType: lead.leadType === "Other" ? lead.customLeadType : "",
      customTraffic: Array.isArray(lead.trafficSource) && lead.trafficSource.includes("Other") ? lead.customTraffic : "",
      customCommission: lead.commissionModel === "Other" ? lead.customCommission : "",
    });
    
    // Устанавливаем голосовую заметку из сервера (если есть)
    setVoiceNoteBlob(null); // Не используем blob для существующих записей
    setVoiceNoteDuration(lead.voiceNoteDuration || 0);
    setVoiceNoteMimeType(lead.voiceNoteMimeType || '');
    
    console.log('[useLeadsLogic] startEdit - Set voiceNoteDuration to:', lead.voiceNoteDuration || 0);
    console.log('[useLeadsLogic] startEdit - Set voiceNoteMimeType to:', lead.voiceNoteMimeType || '');
    
    setEditingId(id);
    setView("edit");
  }

  function onChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }



  async function handleSave(keepFormOpen = false) {
    const v = validateLeadForm(form);
    if (!v.ok) {
      toast.error(v.msg);
      return;
    }

    // Validate notification fields if enabled
    if (form.notificationEnabled) {
      if (!form.notificationDate) {
        toast.error('Укажите дату и время напоминания');
        return;
      }
      // Check if date is in future
      const notifDate = new Date(form.notificationDate);
      if (notifDate <= new Date()) {
        toast.error('Дата напоминания должна быть в будущем');
        return;
      }
    }

    const finalEvent = form.event === "Другое" || form.event === "Other" ? (form.customEvent || "Other") : form.event;

    // Helper: check if photo is Base64 (new) or URL (existing from S3)
    const isBase64Photo = (url) => url.startsWith('data:');
    const hasNewPhotos = (form.photoDataUrls || []).some(isBase64Photo);
    const allPhotosAreNew = (form.photoDataUrls || []).every(isBase64Photo);

    // Prepare API payload
    const apiPayload = {
      event: finalEvent,
      name: form.name,
      company: form.company || null,
      contact: form.contact,
      lead_type: form.leadType,
      custom_lead_type: form.leadType === "Other" ? form.customLeadType : null,
      traffic_source: Array.isArray(form.trafficSource) ? form.trafficSource : [],  // Массив
      custom_traffic_source: (Array.isArray(form.trafficSource) && form.trafficSource.includes("Other")) ? form.customTraffic : null,
      works_with_us: form.worksWithUs,
      commission_model: form.commissionModel,
      custom_commission_model: form.commissionModel === "Other" ? form.customCommission : null,
      notes: form.notes || null,
      notification_enabled: form.notificationEnabled || false,
      notification_date: form.notificationEnabled && form.notificationDate ? form.notificationDate : null,
      notification_message: form.notificationEnabled && form.notificationMessage ? form.notificationMessage : null,
      // New fields
      status: form.status || "to_process",
      priority: form.priority || null,
      partner_id: form.status === "moved_to_affiliate" ? (form.partner_id || null) : null,
      contact_telegram: form.contact_telegram || null,
      contact_whatsapp: form.contact_whatsapp || null,
      contact_wechat: form.contact_wechat || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
    };

    // Обработка фотографий:
    // - При создании: всегда отправляем все фото
    // - При редактировании: отправляем только если есть изменения
    if (!editingId || hasNewPhotos) {
      // Только новые фото (Base64) - существующие S3 URLs игнорируются
      apiPayload.photos = (form.photoDataUrls || [])
        .filter(isBase64Photo)
        .map((dataUrl, index) => ({
          data: dataUrl.split(",")[1], // Remove "data:image/...;base64," prefix
          filename: `photo_${index}.jpg`,
        }));
    }
    // Если при редактировании нет новых фото - не отправляем поле photos (сохраняем существующие)

    try {
      setLoading(true);
      setError(null);

      let savedLeadId = editingId;

      if (editingId) {
        // Update existing lead
        await leadsService.updateLead(editingId, apiPayload);
      } else {
        // Check for duplicates before creating
        const dupeCheck = await leadsService.checkDuplicate(form.contact);
        
        if (dupeCheck.is_duplicate && dupeCheck.existing_lead) {
          const duplicate = dupeCheck.existing_lead;
          
          // Показываем предупреждение с информацией о существующем лиде
          const message = `⚠️ Лид с таким контактом уже был добавлен:\n\n` +
            `Имя: ${duplicate.name}\n` +
            `Компания: ${duplicate.company || 'не указана'}\n` +
            `Контакт: ${duplicate.contact}\n` +
            `Добавлен: @${duplicate.created_by_username} (${duplicate.created_by_name})\n` +
            `Дата: ${duplicate.created_at}\n\n` +
            `Вы все равно можете сохранить этот лид.`;
          
          const createAnyway = confirm(message + "\n\nПродолжить сохранение?");
          
          if (!createAnyway) {
            setLoading(false);
            return; // Cancel creation
          }
        }

        // Create new lead
        const createResponse = await leadsService.createLead(apiPayload);
        savedLeadId = createResponse.data?.id || createResponse.id;
      }

      // Upload voice note if exists
      if (voiceNoteBlob && savedLeadId) {
        try {
          console.log('[useLeadsLogic] Загрузка голосовой заметки...');
          await voiceService.uploadVoiceNote(
            savedLeadId,
            voiceNoteBlob,
            voiceNoteDuration,
            voiceNoteMimeType
          );
          console.log('[useLeadsLogic] Голосовая заметка загружена');
        } catch (voiceErr) {
          console.error('[useLeadsLogic] Ошибка загрузки голосовой заметки:', voiceErr);
          toast.error('Лид сохранен, но голосовая заметка не загрузилась');
          // НЕ прерываем процесс - лид уже создан
        }
      }

      // Reload leads from server
      await loadLeads();
      
      // Clear AI notes backup after successful save
      clearNotesBackup();
      
      toast.success(editingId ? "Лид успешно обновлен" : "Лид успешно создан");
      
      if (keepFormOpen && !editingId) {
        // Для "Сохранить ещё" - очищаем форму и оставляем на странице добавления
        resetForm();
        // view остаётся "add"
      } else {
        // Обычное сохранение - переходим к списку
        setView("list");
        setEditingId(null);
        resetForm();
      }
    } catch (err) {
      console.error("Failed to save lead:", err);
      toast.error(`Ошибка сохранения: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  // Обёртка для кнопки "Сохранить ещё"
  async function handleSaveAndNew() {
    await handleSave(true);
  }

  // Обёртка для кнопки "Сохранить и AI"
  async function handleSaveAndAI() {
    const v = validateLeadForm(form);
    if (!v.ok) {
      toast.error(v.msg);
      return;
    }

    // Validate notification fields if enabled
    if (form.notificationEnabled) {
      if (!form.notificationDate) {
        toast.error('Укажите дату и время напоминания');
        return;
      }
      const notifDate = new Date(form.notificationDate);
      if (notifDate <= new Date()) {
        toast.error('Дата напоминания должна быть в будущем');
        return;
      }
    }

    const finalEvent = form.event === "Другое" || form.event === "Other" ? (form.customEvent || "Other") : form.event;

    // Helper: check if photo is Base64 (new) or URL (existing from S3)
    const isBase64Photo = (url) => url.startsWith('data:');
    const hasNewPhotos = (form.photoDataUrls || []).some(isBase64Photo);

    // Prepare API payload
    const apiPayload = {
      event: finalEvent,
      name: form.name,
      company: form.company || null,
      contact: form.contact,
      lead_type: form.leadType,
      custom_lead_type: form.leadType === "Other" ? form.customLeadType : null,
      traffic_source: Array.isArray(form.trafficSource) ? form.trafficSource : [],
      custom_traffic_source: (Array.isArray(form.trafficSource) && form.trafficSource.includes("Other")) ? form.customTraffic : null,
      works_with_us: form.worksWithUs,
      commission_model: form.commissionModel,
      custom_commission_model: form.commissionModel === "Other" ? form.customCommission : null,
      notes: form.notes || null,
      notification_enabled: form.notificationEnabled || false,
      notification_date: form.notificationEnabled && form.notificationDate ? form.notificationDate : null,
      notification_message: form.notificationEnabled && form.notificationMessage ? form.notificationMessage : null,
      // New fields
      status: form.status || "to_process",
      priority: form.priority || null,
      partner_id: form.status === "moved_to_affiliate" ? (form.partner_id || null) : null,
      contact_telegram: form.contact_telegram || null,
      contact_whatsapp: form.contact_whatsapp || null,
      contact_wechat: form.contact_wechat || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
    };

    if (!editingId || hasNewPhotos) {
      apiPayload.photos = (form.photoDataUrls || [])
        .filter(isBase64Photo)
        .map((dataUrl, index) => ({
          data: dataUrl.split(",")[1],
          filename: `photo_${index}.jpg`,
        }));
    }

    try {
      setLoading(true);
      setError(null);

      let savedLeadId = editingId;

      // Check for duplicates before creating (only for new leads)
      if (!editingId) {
        const dupeCheck = await leadsService.checkDuplicate(form.contact);
        
        if (dupeCheck.is_duplicate && dupeCheck.existing_lead) {
          const duplicate = dupeCheck.existing_lead;
          
          const message = `⚠️ Лид с таким контактом уже был добавлен:\n\n` +
            `Имя: ${duplicate.name}\n` +
            `Компания: ${duplicate.company || 'не указана'}\n` +
            `Контакт: ${duplicate.contact}\n` +
            `Добавлен: @${duplicate.created_by_username} (${duplicate.created_by_name})\n` +
            `Дата: ${duplicate.created_at}\n\n` +
            `Вы все равно можете сохранить этот лид.`;
          
          const createAnyway = confirm(message + "\n\nПродолжить сохранение?");
          
          if (!createAnyway) {
            setLoading(false);
            return; // Cancel - не переходим на AI сканер
          }
        }

        // Create new lead
        const createResponse = await leadsService.createLead(apiPayload);
        savedLeadId = createResponse.data?.id || createResponse.id;
      } else {
        // Update existing lead
        await leadsService.updateLead(editingId, apiPayload);
      }

      // Upload voice note if exists
      if (voiceNoteBlob && savedLeadId) {
        try {
          console.log('[useLeadsLogic] Загрузка голосовой заметки...');
          await voiceService.uploadVoiceNote(
            savedLeadId,
            voiceNoteBlob,
            voiceNoteDuration,
            voiceNoteMimeType
          );
          console.log('[useLeadsLogic] Голосовая заметка загружена');
        } catch (voiceErr) {
          console.error('[useLeadsLogic] Ошибка загрузки голосовой заметки:', voiceErr);
          toast.error('Лид сохранен, но голосовая заметка не загрузилась');
          // НЕ прерываем процесс - лид уже создан
        }
      }

      // Reload leads from server
      await loadLeads();
      
      // Clear AI notes backup after successful save
      clearNotesBackup();
      
      toast.success(editingId ? "Лид успешно обновлен" : "Лид успешно создан");
      
      // Сбрасываем форму и editingId ПЕРЕД переходом на AI сканер
      setEditingId(null);
      resetForm();
      
      // Переходим на AI сканер только после успешного сохранения
      setView("ai-scan");
      
    } catch (err) {
      console.error("Failed to save lead:", err);
      toast.error(`Ошибка сохранения: ${err.message}`);
      // При ошибке НЕ переходим на AI сканер
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!confirm("Удалить лид?")) return;

    try {
      setLoading(true);
      setError(null);

      await leadsService.deleteLead(id);
      
      // Reload leads from server
      await loadLeads();
      
      toast.success("Лид успешно удален");
      
      setView("list");
    } catch (err) {
      console.error("Failed to delete lead:", err);
      toast.error(`Ошибка удаления: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  function handlePhotos(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const maxPhotos = 3;
    const currentPhotos = form.photoDataUrls || [];
    const availableSlots = maxPhotos - currentPhotos.length;
    
    if (availableSlots <= 0) {
      alert(`Максимум ${maxPhotos} фото`);
      return;
    }
    
    const filesToProcess = Array.from(files).slice(0, availableSlots);
    const readers = filesToProcess.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(readers).then((results) => {
      onChange("photoDataUrls", [...currentPhotos, ...results]);
    });
  }
  
  function removePhoto(index) {
    const newPhotos = (form.photoDataUrls || []).filter((_, i) => i !== index);
    onChange("photoDataUrls", newPhotos);
  }

  /**
   * Handle voice note upload
   */
  function handleVoiceNoteUpload(blob, duration, mimeType) {
    console.log('[useLeadsLogic] Voice note uploaded:', { duration, size: blob.size, mimeType });
    setVoiceNoteBlob(blob);
    setVoiceNoteDuration(duration);
    setVoiceNoteMimeType(mimeType);
  }

  /**
   * Handle voice note delete
   */
  async function handleVoiceNoteDelete() {
    console.log('[useLeadsLogic] Deleting voice note, form.id:', form.id, 'voiceNoteUrl:', form.voiceNoteUrl);
    
    if (form.id && form.voiceNoteUrl) {
      // Лид уже сохранен - удаляем с сервера
      try {
        await voiceService.deleteVoiceNote(form.id);
        toast.success('Голосовая заметка удалена');
      } catch (error) {
        console.error('[useLeadsLogic] Ошибка удаления голосовой заметки:', error);
        toast.error('Ошибка удаления голосовой заметки');
      }
    }
    
    // Всегда очищаем локальное состояние (и для новых, и для существующих лидов)
    setVoiceNoteBlob(null);
    setVoiceNoteDuration(0);
    setVoiceNoteMimeType('');
    onChange('voiceNoteUrl', null);
    
    console.log('[useLeadsLogic] Voice note cleared');
  }

  return {
    leads,
    setLeads,
    loading,
    error,
    view,
    setView,
    form,
    startAdd,
    startEdit,
    onChange,
    handleSave,
    handleSaveAndNew,
    handleSaveAndAI,
    handleDelete,
    handlePhotos,
    removePhoto,
    handleVoiceNoteUpload,
    handleVoiceNoteDelete,
    resetForm,
    loadLeads, // Expose for manual refresh
    loadSingleLead, // Expose for deep linking
  };
}
